import moment from 'moment'
import { channelMaps } from '@/constants'
import { PLATFORMS, ACCOUNTS, REGIONS, DOMAINS, CLOUDPROVIDERS, RESOURCES } from '@Bill/constants'
import { getCopyWithContentTableColumn } from '@/utils/common/tableColumn'
import i18n from '@/locales'
import { numerify } from '@/filters'
import BrandIcon from '@/sections/BrandIcon'
import { currencyUnitMap } from '@/constants/currency'
import setting from '@/config/setting'
import { PERIOD_TYPES_MAP, STATUS_MAP, SENDING_FREQUENCYS } from '../constants'

export const getBudgetBrandTableColumn = () => {
  return {
    field: 'brand',
    title: i18n.t('bill.text_81'),
    minWidth: 100,
    sortable: true,
    slots: {
      default: ({ row }, h) => {
        if (row.brand && row.brand !== 'all') {
          return [<BrandIcon name={ row.brand } key={row.id} />]
        }
        return PLATFORMS[0].item_name
      },
    },
  }
}

export const getBudgetAccountTableColumn = () => {
  return getCopyWithContentTableColumn({
    field: 'cloudaccount',
    title: i18n.t('bill.text_60'),
    hideField: true,
    message: (row) => {
      if (row.cloudaccount && row.brand === 'OneCloud') return setting.brand.en || row.cloudaccount
      return row.cloudaccount || ACCOUNTS[0].item_name
    },
    slotCallback: (row) => {
      if (row.cloudaccount && row.brand === 'OneCloud') return setting.brand.en || row.cloudaccount
      return row.cloudaccount || ACCOUNTS[0].item_name
    },
  })
}

export const getBudgetCloudProividerTableColumn = () => {
  return getCopyWithContentTableColumn({
    field: 'cloudprovider_name',
    title: i18n.t('bill.text_233'),
    hideField: true,
    message: (row) => {
      return row.cloudprovider_name || CLOUDPROVIDERS[0].item_name
    },
    slotCallback: (row) => {
      return row.cloudprovider_name || CLOUDPROVIDERS[0].item_name
    },
  })
}

export const getBudgetRegionTableColumn = () => {
  return getCopyWithContentTableColumn({
    field: 'region',
    title: i18n.t('bill.text_83'),
    hideField: true,
    message: (row) => {
      return row.region || REGIONS[0].item_name
    },
    slotCallback: (row) => {
      return row.region || REGIONS[0].item_name
    },
  })
}

export const getBudgetDomainTableColumn = () => {
  return getCopyWithContentTableColumn({
    field: 'domain_filter',
    title: i18n.t('dictionary.domain'),
    hideField: true,
    message: (row) => {
      return row.domain_filter || DOMAINS[0].item_name
    },
    slotCallback: (row) => {
      return row.domain_filter || DOMAINS[0].item_name
    },
  })
}

export const getBudgetProjectTableColumn = () => {
  return {
    field: 'project',
    title: i18n.t('bill.budget_project'),
    slots: {
      default: ({ row }, h) => {
        if (!row.tenant && !row.project_domain) {
          return i18n.t('monitor.text00024')
        }
        const domain = row.project_domain || row.domain
        if (!row.tenant && domain) {
          return `${domain}${i18n.t('monitor.text00025')}`
        }
        if (row.tenant && domain) {
          return [
            <list-body-cell-wrap copy field='tenant' row={row} />,
            <list-body-cell-wrap hide-field copy field="domain" row={{ domain }}>
              <span class='text-weak'>{ domain }</span>
            </list-body-cell-wrap>,
          ]
        }
      },
    },
  }
}

export const getBudgetResourceTypeTableColumn = () => {
  return getCopyWithContentTableColumn({
    field: 'resource_type',
    title: i18n.t('bill.text_73'),
    hideField: true,
    message: (row) => {
      if (row.resource_type && row.resource_type !== 'all') {
        return row.resource_type
      }
      return RESOURCES[0].item_name
    },
    slotCallback: (row) => {
      if (row.resource_type && row.resource_type !== 'all') {
        return row.resource_type
      }
      return RESOURCES[0].item_name
    },
  })
}

export const getBudgetStatusTableColumn = ({ field = 'budget_status' } = {}) => {
  return {
    field,
    title: i18n.t('bill.text_89'),
    minWidth: 70,
    showOverflow: 'title',
    slots: {
      default: ({ row }, h) => {
        let status = ''
        const now = moment()
        if (now.isSameOrBefore(row.start_time)) {
          status = 'ready'
        } else if (now.isSameOrAfter(row.end_time)) {
          status = 'expired'
        } else {
          status = 'running'
        }
        return STATUS_MAP[status] && STATUS_MAP[status].value
      },
    },
  }
}

export const getBudgetCycleTableColumn = ({ field = 'period_type' } = {}) => {
  return {
    field,
    title: i18n.t('bill.budget_period_type'),
    minWidth: 70,
    showOverflow: 'title',
    slots: {
      default: ({ row }, h) => {
        return PERIOD_TYPES_MAP[row[field]].value
      },
    },
  }
}

export const getBillingTypeTableColumn = ({ field = 'currency' } = {}) => {
  return {
    field,
    title: i18n.t('bill.budget_currency'),
    minWidth: 70,
    showOverflow: 'title',
    slots: {
      default: ({ row }, h) => {
        // const currencyLabel = CURRENCYS_MAP[row[field]] && CURRENCYS_MAP[row[field]].value
        return row.is_exchanged_currency ? i18n.t('bill.text_287', [row.currency]) : i18n.t('bill.text_39', [row.currency])
      },
    },
  }
}

export const getBudgetAmountTableColumn = ({ field = 'amount' } = {}) => {
  return {
    field,
    title: i18n.t('bill.budget_amount'),
    minWidth: 70,
    showOverflow: 'title',
    align: 'right',
    headerAlign: 'right',
    width: 100,
    slots: {
      default: ({ row }, h) => {
        const money = numerify(row[field], '0,0.00')
        const currencySign = currencyUnitMap[row.currency]?.sign || currencyUnitMap.CNY.sign
        return [<div title={ row[field] }>{currencySign} {money}</div>]
      },
    },
  }
}

export const getBudgetStartDateTableColumn = () => {
  return {
    field: 'start_date',
    title: i18n.t('common.text00119'),
    minWidth: 70,
    showOverflow: 'title',
    slots: {
      default: ({ row }, h) => {
        return row.start_date
      },
    },
  }
}

export const getBudgetEndDateTableColumn = () => {
  return {
    field: 'end_date',
    title: i18n.t('common.text00120'),
    minWidth: 70,
    showOverflow: 'title',
    slots: {
      default: ({ row }, h) => {
        return row.end_date
      },
    },
  }
}

export const getVerifiedContactTypesTableColumn = ({ field = 'channel', title = i18n.t('common_599'), vm } = {}) => {
  return {
    title: i18n.t('common_599'),
    field: 'channel',
    minWidth: 120,
    slots: {
      default: ({ row }, h) => {
        const color = '#52c41a'
        let channel = []
        if (row.alerts && row.alerts.length > 0 && row.alerts[0].channel) {
          channel = row.alerts[0].channel
        }
        const renderComponents = []
        channel.forEach((ctype) => {
          switch (ctype) {
            case 'webconsole':
              renderComponents.push(<icon type='webconsole' style={{ color: color }} title={i18n.t('dictionary.webconsole')} />)
              break
            case 'email':
              renderComponents.push(<icon class='ml-2' type='email' style={{ color: color }} title={i18n.t('common.email')} />)
              break
            case 'mobile':
              renderComponents.push(<icon class='ml-2' type='mobile' style={{ color: color }} title={i18n.t('common.mobile')} />)
              break
            case 'dingtalk':
              renderComponents.push(<icon class='ml-2' type='dingtalk' style={{ color: color }} title={i18n.t('common.dingtalk')} />)
              break
            case 'feishu':
              renderComponents.push(<icon class='ml-2' type='feishu' style={{ color: color }} title={i18n.t('common.feishu')} />)
              break
            case 'workwx':
              renderComponents.push(<icon class='ml-2' type='workwx' style={{ color: color }} title={i18n.t('common.workwx')} />)
              break
            default:
              break
          }
        })
        return renderComponents
      },
    },
  }
}

export const conditionColumn = () => {
  return {
    field: 'channel',
    title: i18n.t('monitor.text_11'),
    minWidth: 80,
    formatter: ({ row }) => {
      let channel = []
      if (row.alerts && row.alerts.length > 0 && row.alerts[0].channel) {
        channel = row.alerts[0].channel
      }
      if (channel.length <= 0) return '-'
      return channel.filter(val => ~val.indexOf('robot')).map(val => (channelMaps[val] || {}).label).join('、')
    },
  }
}

export const alertFrequencyColumn = () => {
  return {
    field: 'alert_frequency',
    title: i18n.t('bill.alert_frequency'),
    minWidth: 80,
    formatter: ({ row }) => {
      if (row.alerts && row.alerts.length > 0 && row.alerts[0].alert_frequency) {
        const freq = SENDING_FREQUENCYS.find((item) => item.key === row.alerts[0].alert_frequency)
        if (freq) {
          return freq.value
        }
      }

      return '-'
    },
  }
}
